import { useTRPC } from "../../../trpc";
import { Card, Loader, Stack, Switch } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { MapLibreSplitPanelExample } from "@mapka/configurator";
import { createTilesetExample, getExampleStyle } from "./utils/example";
import { useDefaultApiKey } from "../../../hooks/apiKey";
import { useQuery } from "@tanstack/react-query";
import { tilesetDashboardLayoutRoute } from "./routes";
import { useCallback, useState } from "react";
import type { ChangeEventHandler } from "react";
import type { StyleSpecification } from "@maplibre/maplibre-gl-style-spec";
import { usePathAccountName } from "../../../hooks/router";

const emptyStyle: StyleSpecification = {
  version: 8,
  layers: [],
  sources: {},
};

export function TilesetOverview() {
  const intl = useIntl();
  const trpc = useTRPC();
  const apiKey = useDefaultApiKey();
  const account = usePathAccountName();

  const [showBaseMap, setShowBasemap] = useState(true);

  const { tileset } = tilesetDashboardLayoutRoute.useParams();
  const { dataset } = tilesetDashboardLayoutRoute.useSearch();

  const { data: tilesetWithVersions } = useQuery(trpc.getTilesetWithVersions.queryOptions({
    tileset,
    dataset,
    account,
  }, {
    refetchInterval: 1000 * 60 * 5,
    refetchOnMount: false,
  }));

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setShowBasemap(event.target.checked);
  }, []);

  const exampleStyle = getExampleStyle();
  const mapLibreCodeExample = createTilesetExample(tilesetWithVersions, apiKey);

  return (
    <Stack gap={16} px="md" my="md">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Overview", id: "navigation.overview" })}
        current={dataset ? `${dataset} - ${tileset}` : tileset}
        description={
          <FormattedMessage id="text.tilesetOverviewDescription" defaultMessage="View and manage your tileset" />
        }
      />
      <Stack gap={8}>
        <Switch
          checked={showBaseMap}
          onChange={handleOnChange}
          label={
            <FormattedMessage id="label.showBasemap" defaultMessage="Show basemap" />
          }
        />
        <Card shadow="sm" padding="0px" radius="md" withBorder w="100%" h="860px">
          {mapLibreCodeExample && showBaseMap && (
            <MapLibreSplitPanelExample
              key="map"
              example={mapLibreCodeExample}
              style={exampleStyle}
              mapkaApiKey={apiKey}
            />
          )}
          {mapLibreCodeExample && !showBaseMap && (
            <MapLibreSplitPanelExample
              key="empty"
              example={mapLibreCodeExample}
              style={emptyStyle}
              mapkaApiKey={apiKey}
            />
          )}
          {!mapLibreCodeExample && (
            <Stack
              align="center"
              justify="center"
              h="100%"
              w="100%"
            >
              <Loader size="lg" />
            </Stack>
          )}
        </Card>

      </Stack>
    </Stack>
  );
}
